import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { BookingRequest } from '@/api/pending'
import { useAccess } from '@/access/useAccess'
import { ago, fmtDate } from '@/lib/format'
import { Button, Card, EmptyRow, PageHeader, Pill, Row, Segmented, Spinner } from '@/ui'
import { bookingRequestsQuery } from './api'
import { BookingReviewModal } from './BookingReviewModal'
import { RejectBookingModal } from './RejectBookingModal'
import { STATUS_PILL } from './review'
import styles from './BookingsView.module.css'

type Tab = 'pending' | 'approved' | 'rejected'

export function BookingsView() {
  const { condoId } = useAccess()
  const [tab, setTab] = useState<Tab>('pending')
  const [reviewing, setReviewing] = useState<number | null>(null)
  const [rejecting, setRejecting] = useState<number | null>(null)

  const list = useQuery(bookingRequestsQuery(condoId, tab))
  const pending = useQuery(bookingRequestsQuery(condoId, 'pending'))

  const rows = list.data ?? []
  // The modals read the live row, so a refetch (or a review save) is reflected without reopening.
  const reviewed = rows.find((b) => b.id === reviewing)
  const rejected = rows.find((b) => b.id === rejecting)

  return (
    <div>
      <PageHeader
        title="Reservas"
        subtitle="Confirme ou recuse solicitações das áreas comuns. Aprovar bloqueia o horário para os demais."
      />
      <Segmented<Tab>
        aria-label="Situação das reservas"
        value={tab}
        onChange={setTab}
        items={[
          { id: 'pending', label: 'Solicitações', count: pending.data?.length },
          { id: 'approved', label: 'Aprovadas' },
          { id: 'rejected', label: 'Recusadas' },
        ]}
      />
      <Card pad={0} className={styles.table}>
        <Row header>
          <div className={styles.col}>Área</div>
          <div className={styles.col}>Morador</div>
          <div className={styles.col}>Quando</div>
          <div className={styles.status}>Status</div>
          <div className={styles.actions} />
        </Row>
        {list.isPending ? (
          <div className={styles.center}>
            <Spinner size={24} />
          </div>
        ) : list.isError ? (
          <div className={styles.center}>
            <p className={styles.error}>Não foi possível carregar as reservas.</p>
            <Button variant="secondary" size="s" onClick={() => list.refetch()}>
              Tentar de novo
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyRow
            icon="calendar_month"
            title="Nenhuma reserva aqui"
            description="As solicitações aparecem quando os moradores escolhem data e horário no app."
          />
        ) : (
          rows.map((b) => (
            <BookingRow key={b.id} b={b} onReview={setReviewing} onReject={setRejecting} />
          ))
        )}
      </Card>
      {reviewed && <BookingReviewModal booking={reviewed} onClose={() => setReviewing(null)} />}
      {rejected && <RejectBookingModal booking={rejected} onClose={() => setRejecting(null)} />}
    </div>
  )
}

function BookingRow({
  b,
  onReview,
  onReject,
}: {
  b: BookingRequest
  onReview: (id: number) => void
  onReject: (id: number) => void
}) {
  const pill = STATUS_PILL[b.status]
  return (
    <Row className={styles.row}>
      <div className={styles.col}>
        <div className={styles.primary}>{b.area}</div>
        {b.note && <div className={styles.secondary}>{b.note}</div>}
      </div>
      <div className={styles.col}>
        <div className={styles.primary}>{b.resident}</div>
        <div className={styles.secondary}>{b.unit}</div>
      </div>
      <div className={styles.col}>
        <div className={styles.primary}>{fmtDate(b.date)}</div>
        <div className={styles.secondary}>
          {b.start_time}–{b.end_time}
        </div>
      </div>
      <div className={styles.status}>
        <Pill tone={pill.tone}>{pill.label}</Pill>
      </div>
      <div className={styles.actions}>
        {b.status === 'pending' ? (
          <>
            <Button
              size="s"
              variant="secondary"
              aria-label={`Recusar ${b.area} de ${b.resident}`}
              onClick={() => onReject(b.id)}
            >
              Recusar
            </Button>
            <Button
              size="s"
              aria-label={`Aprovar ${b.area} de ${b.resident}`}
              onClick={() => onReview(b.id)}
            >
              Aprovar
            </Button>
          </>
        ) : b.status === 'approved' ? (
          <button
            type="button"
            className={styles.reviewLink}
            aria-label={`Ver revisão de ${b.area} de ${b.resident}`}
            onClick={() => onReview(b.id)}
          >
            Confirmada {ago(b.approved_at ?? b.requested_at)}
          </button>
        ) : (
          <span className={styles.secondary}>Recusada</span>
        )}
      </div>
    </Row>
  )
}
